import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { fetchDashboardData, fetchDiscoveryStats } from '../../services/Http/Api';
import {
    MdAttachMoney,
    MdShowChart,
    MdPeople,
    MdStoreMallDirectory,
    MdBusiness,
    MdReceipt,
    MdShoppingCart,
    MdMonetizationOn,
    MdPieChart,
    MdAccountBalance,
    MdDashboard,
    MdInventory2,
    MdLocalOffer,
    MdSync,
    MdVisibility,
    MdOpenInNew,
    MdTouchApp,
    MdErrorOutline
} from 'react-icons/md';
import styled from 'styled-components';
import PageContainer from '../../shared/layout/containers/PageContainer';
import PageHeader from '../../shared/layout/headers/PageHeader';
import PageTitle from '../../shared/layout/titles/PageTitle';
import StatsGrid from '../../shared/layout/containers/StatsGrid';
import StatCard from '../../shared/layout/cards/StatCard';
import PageContent from '../../shared/layout/containers/PageContent';
import LoadingMessage from '../../shared/layout/feedback/LoadingMessage';
import DashboardSkeleton from '../../components/common/skeletons/DashboardSkeleton';
import ErrorMessage from '../../shared/layout/feedback/ErrorMessage';
import MainButton from '../../shared/layout/buttons/MainButton';
import { useCurrency } from '../../context/CurrencyProvider';

function Dashboard() {
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();
    const navigate = useNavigate();
    const [dashboardData, setDashboardData] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [onboarding, setOnboarding] = useState(null);
    const [retailerOverview, setRetailerOverview] = useState(null);
    const [engagementStats, setEngagementStats] = useState(null);
    const hasDashboardActivity = Object.values(dashboardData || {}).some((value) => {
        const amount = value && typeof value === 'object'
            ? value.localAmount ?? value.eurAmount
            : value;
        return Number.isFinite(Number(amount)) && Number(amount) !== 0;
    });

    useEffect(() => {
        const loadDashboardData = async () => {
            try {
                const data = await fetchDashboardData();
                if (data?.onboarding?.required) {
                    navigate('/setup', { replace: true });
                    return;
                }
                setOnboarding(data?.onboarding || null);
                setRetailerOverview(data?.retailer_overview || null);
                const counters = Array.isArray(data) ? data : data?.counters;
                const nextSuggestions = Array.isArray(data?.suggestions) ? data.suggestions : [];
                setSuggestions(nextSuggestions);

                if (Array.isArray(counters) && counters.length > 0) {
                    const filteredData = filterData(counters[0]);
                    setDashboardData(filteredData);
                } else {
                    setDashboardData({});
                }

                if (data?.retailer_overview) {
                    fetchDiscoveryStats({ days: 30 })
                        .then(setEngagementStats)
                        .catch(() => setEngagementStats(null));
                }
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        loadDashboardData();
    }, []);



    if (error) {
        return <ErrorMessage>{t('Dashboard.error')}: {error.message}</ErrorMessage>;
    }

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle icon={<MdDashboard size={28} />}>{t('Dashboard.title')}</PageTitle>
            </PageHeader>
            <PageContent>
                {loading ? (
                    <DashboardSkeleton />
                ) : (
                    <>
                        {onboarding?.enrolled && !onboarding.completed && <MainButton onClick={() => navigate('/setup')}>{t('RetailerSetup.resume')}</MainButton>}
                        {retailerOverview && (
                            <RetailerDashboard>
                                <DashboardSectionHeader>
                                    <div>
                                        <SuggestionHeader>{t('Dashboard.retailerOverview')}</SuggestionHeader>
                                        <SetupIntro>{t('Dashboard.retailerOverviewDescription')}</SetupIntro>
                                    </div>
                                    <DashboardActions>
                                        <MainButton size="small" variant="secondary" onClick={() => navigate('/catalog-import')}>
                                            {t('Dashboard.manageCatalog')}
                                        </MainButton>
                                        <MainButton size="small" variant="secondary" onClick={() => navigate('/discovery-stats')}>
                                            {t('Dashboard.viewAnalytics')}
                                        </MainButton>
                                    </DashboardActions>
                                </DashboardSectionHeader>

                                <RetailerMetricsGrid>
                                    <StatCard
                                        icon={<MdInventory2 size={26} />}
                                        title={t('Dashboard.catalogProducts')}
                                        value={retailerOverview.catalog_unlimited
                                            ? `${formatNumberValue(retailerOverview.catalog_products)} · ${t('Dashboard.unlimited')}`
                                            : `${formatNumberValue(retailerOverview.catalog_products)} / ${formatNumberValue(retailerOverview.catalog_limit)}`}
                                        color="campaigns"
                                    />
                                    <StatCard icon={<MdStoreMallDirectory size={26} />} title={t('Dashboard.availableProducts')} value={formatNumberValue(retailerOverview.available_products)} color="soldItems" />
                                    <StatCard icon={<MdLocalOffer size={26} />} title={t('Dashboard.activeDiscounts')} value={formatNumberValue(retailerOverview.active_discounts)} color="transactions" />
                                    <StatCard icon={<MdVisibility size={26} />} title={t('Dashboard.discountImpressions')} value={formatOptionalNumber(engagementStats?.totals?.impressions ?? engagementStats?.totals?.viewed)} color="campaigns" />
                                    <StatCard icon={<MdOpenInNew size={26} />} title={t('Dashboard.discountDetailViews')} value={formatOptionalNumber(engagementStats?.totals?.detail_views)} color="info" />
                                    <StatCard icon={<MdTouchApp size={26} />} title={t('Dashboard.discountClicks')} value={formatOptionalNumber(engagementStats?.totals?.clicks)} color="transactions" />
                                    <StatCard icon={<MdVisibility size={26} />} title={t('Dashboard.catalogImpressions')} value={formatOptionalNumber(engagementStats?.catalog?.impressions)} color="campaigns" />
                                    <StatCard icon={<MdOpenInNew size={26} />} title={t('Dashboard.catalogDetailViews')} value={formatOptionalNumber(engagementStats?.catalog?.views)} color="info" />
                                    <StatCard icon={<MdTouchApp size={26} />} title={t('Dashboard.catalogClicks')} value={formatOptionalNumber(engagementStats?.catalog?.clicks)} color="transactions" />
                                </RetailerMetricsGrid>

                                <SyncSummary>
                                    <SyncIcon><MdSync size={22} /></SyncIcon>
                                    <SyncCopy>
                                        <SyncTitle>{t('Dashboard.catalogSync')}</SyncTitle>
                                        <SyncDescription>
                                            {retailerOverview.sync
                                                ? t(`Dashboard.syncStatus_${retailerOverview.sync.status}`, { defaultValue: t('Dashboard.syncStatus_unknown') })
                                                : t('Dashboard.noSyncYet')}
                                            {retailerOverview.sync?.started_at ? ` · ${new Date(retailerOverview.sync.started_at).toLocaleString()}` : ''}
                                        </SyncDescription>
                                    </SyncCopy>
                                    {retailerOverview.sync && (
                                        <SyncResult>
                                            <span>{t('Dashboard.syncCreated', { count: formatNumberValue(retailerOverview.sync.created) })}</span>
                                            <span>{t('Dashboard.syncUpdated', { count: formatNumberValue(retailerOverview.sync.updated) })}</span>
                                            {Number(retailerOverview.sync.errors_count) > 0 && (
                                                <ErrorCount><MdErrorOutline /> {t('Dashboard.syncErrors', { count: formatNumberValue(retailerOverview.sync.errors_count) })}</ErrorCount>
                                            )}
                                        </SyncResult>
                                    )}
                                </SyncSummary>
                            </RetailerDashboard>
                        )}
                        {suggestions.length > 0 && (
                            <SuggestionSection>
                                <SetupHeader>
                                    <div>
                                        <SuggestionHeader>{t('Dashboard.setup_title')}</SuggestionHeader>
                                        <SetupIntro>{t('Dashboard.setup_intro')}</SetupIntro>
                                    </div>
                                    <RemainingCount>{t('Dashboard.setup_remaining', { count: suggestions.length })}</RemainingCount>
                                </SetupHeader>
                                <NextStep>
                                    <NextStepLabel>{t('Dashboard.setup_next')}</NextStepLabel>
                                    <SuggestionText>
                                        <SuggestionTitle>{t(suggestions[0].title_key)}</SuggestionTitle>
                                        <SuggestionDescription>{t(suggestions[0].description_key)}</SuggestionDescription>
                                    </SuggestionText>
                                    <MainButton size="small" onClick={() => navigate(suggestions[0].href)}>
                                        {t(suggestions[0].cta_key)}
                                    </MainButton>
                                </NextStep>
                                {suggestions.length > 1 && (
                                    <OtherSteps>
                                        <summary>{t('Dashboard.setup_other_steps', { count: suggestions.length - 1 })}</summary>
                                        <SuggestionList>
                                            {suggestions.slice(1).map((item) => (
                                                <SuggestionRow key={item.key}>
                                                    <SuggestionText>
                                                        <SuggestionTitle>{t(item.title_key)}</SuggestionTitle>
                                                        <SuggestionDescription>{t(item.description_key)}</SuggestionDescription>
                                                    </SuggestionText>
                                                    <MainButton size="small" variant="secondary" onClick={() => navigate(item.href)}>
                                                        {t(item.cta_key)}
                                                    </MainButton>
                                                </SuggestionRow>
                                            ))}
                                        </SuggestionList>
                                    </OtherSteps>
                                )}
                            </SuggestionSection>
                        )}
                        {suggestions.length === 0 && !retailerOverview && (
                            <SetupComplete>
                                <SuggestionHeader>
                                    {t('Dashboard.setup_complete_title')}
                                </SuggestionHeader>
                                <SuggestionDescription>
                                    {t('Dashboard.setup_complete_description')}
                                </SuggestionDescription>
                            </SetupComplete>
                        )}
                        {!retailerOverview && (suggestions.length === 0 || hasDashboardActivity) && (
                            <StatsGrid>
                                {dashboardData && Object.entries(dashboardData).map(([key, value]) => (
                                    <StatCard
                                        key={key}
                                        icon={selectIcon(key)}
                                        title={t(`Dashboard.${key}`)}
                                        value={formatNumber(key, value, formatResolvedAmount)}
                                        color={selectColor(key)}
                                    />
                                ))}
                            </StatsGrid>
                        )}
                    </>
                )}
            </PageContent>
        </PageContainer>
    );
}

const filterData = (data) => {
    const keysToExtract = [
        'average_revenue_per_user',
        'average_selling_transaction_value',
        'belonging_users_count',
        'campaigns_count',
        'manufacturer_count',
        'revenue_value',
        'selling_transactions_count',
        'sold_items_count',
        'total_transaction_volume',
        'users_count',
        'venues_count'
    ];

    const monetaryLocalKeyMap = {
        average_revenue_per_user: 'average_revenue_per_user_local',
        average_selling_transaction_value: 'average_selling_transaction_value_local',
        revenue_value: 'revenue_value_local',
        total_transaction_volume: 'total_transaction_volume_local'
    };

    return Object.fromEntries(
        keysToExtract.map((key) => {
            if (Object.prototype.hasOwnProperty.call(monetaryLocalKeyMap, key)) {
                return [
                    key,
                    {
                        eurAmount: data?.[key],
                        localAmount: data?.[monetaryLocalKeyMap[key]],
                        localCurrencyId: data?.currency_id
                    }
                ];
            }

            return [key, data?.[key]];
        })
    );
};

const formatNumberValue = (value) => Number(value || 0).toLocaleString();
const formatOptionalNumber = (value) => (value === null || value === undefined ? '—' : formatNumberValue(value));

const formatNumber = (key, value, formatResolvedAmount) => {
    const floatKeys = ['average_revenue_per_user', 'average_selling_transaction_value', 'revenue_value', 'total_transaction_volume'];
    if (floatKeys.includes(key)) {
        return formatResolvedAmount({
            eurAmount: value?.eurAmount,
            localAmount: value?.localAmount,
            localCurrencyId: value?.localCurrencyId
        });
    }
    return parseInt(value || 0, 10).toLocaleString();
};

const selectIcon = (key) => {
    const iconMapping = {
        'average_revenue_per_user': <MdAttachMoney size={50} className="icon" />,
        'average_selling_transaction_value': <MdShowChart size={50} className="icon" />,
        'belonging_users_count': <MdPeople size={50} className="icon" />,
        'campaigns_count': <MdPieChart size={50} className="icon" />,
        'manufacturer_count': <MdBusiness size={50} className="icon" />,
        'revenue_value': <MdReceipt size={50} className="icon" />,
        'selling_transactions_count': <MdShoppingCart size={50} className="icon" />,
        'sold_items_count': <MdStoreMallDirectory size={50} className="icon" />,
        'total_transaction_volume': <MdMonetizationOn size={50} className="icon" />,
        'users_count': <MdPeople size={50} className="icon" />,
        'venues_count': <MdAccountBalance size={50} className="icon" />
    };
    return iconMapping[key] || <MdShowChart size={50} className="icon" />;
};

const selectColor = (key) => {
    const colorMapping = {
        'average_revenue_per_user': 'revenue',
        'average_selling_transaction_value': 'transactionValue',
        'belonging_users_count': 'belongingUsers',
        'campaigns_count': 'campaigns',
        'manufacturer_count': 'manufacturer',
        'revenue_value': 'revenueValue',
        'selling_transactions_count': 'transactions',
        'sold_items_count': 'soldItems',
        'total_transaction_volume': 'totalVolume',
        'users_count': 'users',
        'venues_count': 'venues'
    };
    return colorMapping[key] || 'neutral';
};

const SuggestionSection = styled.section`
    margin-bottom: 1.5rem;
    padding: 1.25rem 1.5rem;
    border: 1px solid #dfe6e4;
    border-radius: 6px;
    background: #fff;
`;

const SuggestionHeader = styled.h3`
    margin: 0;
    font-size: 1.15rem;
    font-weight: 650;
    color: #1b2925;
`;

const SetupHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    @media (max-width: 600px) { flex-direction: column; }
`;

const SetupIntro = styled.p`
    margin: 0.35rem 0 0;
    color: #65736f;
    font-size: 0.9rem;
    line-height: 1.5;
`;

const RemainingCount = styled.span`
    flex: 0 0 auto;
    color: #4f625c;
    font-size: 0.82rem;
    font-weight: 600;
`;

const NextStep = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 0.7rem 1.25rem;
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #e8edeb;
    button { grid-column: 2; grid-row: 2; }
    @media (max-width: 600px) {
        grid-template-columns: 1fr;
        button { grid-column: 1; grid-row: auto; width: 100%; }
    }
`;

const NextStepLabel = styled.span`
    grid-column: 1 / -1;
    margin-bottom: -0.35rem;
    color: #28644d;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
`;

const SuggestionList = styled.div`
    display: flex;
    flex-direction: column;
`;

const SuggestionRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.9rem 0;
    border-top: 1px solid #e8edeb;

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: flex-start;

        button {
            width: 100%;
        }
    }
`;

const OtherSteps = styled.details`
    margin-top: 1rem;
    border-top: 1px solid #e8edeb;
    summary {
        padding-top: 0.9rem;
        color: #355c4f;
        font-size: 0.88rem;
        font-weight: 600;
        cursor: pointer;
    }
    ${SuggestionList} { margin-top: 0.1rem; }
`;

const SetupComplete = styled.section`
    margin-bottom: 1.5rem;
    padding: 1rem 0;
    border-bottom: 1px solid #e5e9e7;
    & > div { margin-top: 0.35rem; }
`;

const RetailerDashboard = styled.section`
    margin-bottom: 1.5rem;
`;

const DashboardSectionHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1rem;

    @media (max-width: 700px) { flex-direction: column; }
`;

const DashboardActions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
`;

const RetailerMetricsGrid = styled(StatsGrid)`
    grid-template-columns: repeat(3, minmax(0, 1fr));

    @media (max-width: 1100px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    @media (max-width: 560px) { grid-template-columns: minmax(0, 1fr); }
`;

const SyncSummary = styled.div`
    display: flex;
    align-items: center;
    gap: 0.9rem;
    margin-top: 1rem;
    padding: 1rem 1.1rem;
    border: 1px solid ${({ theme }) => theme.colors.border || '#e5e7eb'};
    border-radius: 10px;
    background: ${({ theme }) => theme.colors.surface || '#fff'};

    @media (max-width: 700px) { align-items: flex-start; flex-wrap: wrap; }
`;

const SyncIcon = styled.span`
    display: grid;
    place-items: center;
    width: 2.5rem;
    height: 2.5rem;
    flex: 0 0 auto;
    border-radius: 0.75rem;
    color: #177e89;
    background: #eaf7f7;
`;

const SyncCopy = styled.div`
    min-width: 180px;
    flex: 1;
`;

const SyncTitle = styled.div`
    color: #263238;
    font-weight: 700;
`;

const SyncDescription = styled.div`
    margin-top: 0.2rem;
    color: #667085;
    font-size: 0.88rem;
`;

const SyncResult = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.45rem 1rem;
    color: #475467;
    font-size: 0.85rem;
`;

const ErrorCount = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    color: #b42318;
`;

const SuggestionText = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;

    @media (max-width: 768px) {
        flex-wrap: wrap;
    }
`;

const SuggestionTitle = styled.div`
    font-weight: 700;
    color: #334155;
    font-size: 1rem;
`;

const SuggestionDescription = styled.div`
    color: #64748b;
    font-size: 0.95rem;
    line-height: 1.5;
`;

export default Dashboard;
